// todo: fix this !!!
/* eslint-disable @typescript-eslint/no-unsafe-assignment */

'use client';

import 'swiper/css';
import 'swiper/css/pagination';

import type {
  AdvertisementResponse,
  ApiHttpException,
  Customer,
  DealResponse,
  Offer,
  ShortOffer,
} from '@dobrozem/sdk';
import { advertisementServiceDeleteAdvertisement, offerServiceProposePrice } from '@dobrozem/sdk';
import { Close } from '@radix-ui/react-dialog';
import { useMutation } from '@tanstack/react-query';
import clsx from 'clsx';
import Image from 'next/image';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import type { SetStateAction } from 'react';
import { useEffect, useState } from 'react';
import { Autoplay, Pagination } from 'swiper/modules';
import type { SwiperClass } from 'swiper/react';
import { Swiper, SwiperSlide } from 'swiper/react';

import eyeIcon from '@/assets/icons/eye-icon.svg';
import heartIcon from '@/assets/icons/heart.svg';
import dobrozemOk from '@/assets/img/dobrozem-ok.svg';
import { CloseIcon } from '@/assets/react-icons/CloseIcon';
import { DeleteIcon } from '@/assets/react-icons/DeleteIcon';
import { EditIcon } from '@/assets/react-icons/EditIcon';
import { HeartIcon } from '@/assets/react-icons/HeartIcon';
import { MapPinIcon } from '@/assets/react-icons/MapPinIcon';
import { SliderControlLeft } from '@/assets/react-icons/SliderControlLeft';
import { SliderControlRight } from '@/assets/react-icons/SliderControlRight';
import { AuthorizedInfo } from '@/components/AuthorizedInfo';
import { Button } from '@/components/Button';
import { ContactSupportModal } from '@/components/ContactSupportModal';
import { Container } from '@/components/Container';
import { Input } from '@/components/Input';
import { Loader } from '@/components/Loader';
import { Modal } from '@/components/Modal';
import { OfferModalBtn } from '@/components/OfferModalBtn';
import { OfferTag } from '@/components/OfferTag';
import { Responsive } from '@/components/Responsive';
import { StatusModal } from '@/components/StatusModal';
import { Text } from '@/components/Text';
import { TooltipComponent } from '@/components/Tooltip';
import { IdField } from '@/components/IdField';
import { useUser } from '@/contexts/AuthProvider';
import { useFavorites } from '@/hooks/useFavorites';
import { clientSdk } from '@/lib/sdk';
import {
  beautifyNumber,
  dateFormatterShort,
  decodeHtmlEntities,
  formatNumberValue,
  getOrderedAdvertisementLandPhotos,
  isNoDataUpload,
  technicalFeaturesDictionary,
  uploadToSrc,
} from '@/utils/common';
import { getAdvertisementArea } from '@/utils/getAdvertisementArea';

import { BuyersDeal } from './BuyersDeal';
import { DealHistory } from './DealHistory';
import styles from './OfferView.module.scss';

function getUserType(data: DealResponse): 'buyer' | 'seller' | null {
  if (data.seller) {
    return 'seller';
  } else if (data.buyer) {
    return 'buyer';
  }

  return null;
}

export const OfferView = ({
  advertisement,
  currentDeal,
  deals,
}: {
  advertisement: AdvertisementResponse;
  currentDeal?: DealResponse;
  deals?: ShortOffer[];
}) => {
  const [isOpenModal, setIsOpenModal] = useState(false);

  const [isOpenDeleteModal, setIsOpenDeleteModal] = useState(false);

  const [isOpenDeleteInDealModal, setIsOpenDeleteInDealModal] = useState(false);

  const [isOpenContactSupportModal, setIsOpenContactSupportModal] = useState(false);

  const [isOpenFavoriteModalNotLoginned, setOpenFavoriteModalNotLoginned] = useState(false);

  const [isOpenPriceProposalModalNotLoginned, setOpenPriceProposalModalNotLoginned] =
    useState(false);

  const [isOpenFavoriteDeleteModal, setOpenFavoriteDeleteModal] = useState(false);

  const [favoriteRemovableId, setFavoriteRemovableId] = useState<null | string>(null);

  const [selectedImageIndex, setSelectImageIndex] = useState(0);

  const [proposalPrice, setProposalPrice] = useState<string>();

  const [checkError, setCheckError] = useState(false);

  const [offerProposalSuccessSended, setOfferProposalSuccessSended] = useState(false);

  const [offerProposalErrorMessage, setOfferProposalErrorMessage] = useState('');

  const [sliderControls, setSliderControls] = useState<SwiperClass | null>(null);

  const [clientUser, setClientUser] = useState<SetStateAction<Customer | null>>(null);

  const { addFavorite, favorites, removeFavorite } = useFavorites();

  const user = useUser();

  const router = useRouter();

  const pagination = {
    clickable: true,
    renderBullet: function (_index: number, className: string) {
      return `<span class="${className}"><span></span></span>`;
    },
  };

  const proposalMutation = useMutation({
    mutationFn: offerServiceProposePrice<false>,
    onSuccess: ({ data, error }) => {
      if (data?.success) {
        setOfferProposalSuccessSended(true);
      }

      switch (true) {
        case (error as ApiHttpException).errorCode === 'ALREADY_EXISTS':
          setOfferProposalErrorMessage('Пропозиція вже була надіслана');
          break;
        case (error as ApiHttpException).errorCode === 'NOT_FOUND':
          setOfferProposalErrorMessage('Оголошення не доступне для пропозиції');
          break;
        case (error as ApiHttpException).message[0] === 'price must not be less than 1':
          setOfferProposalErrorMessage('Ціна не повинна бути менше 1 грн');
          break;
        case (error as ApiHttpException).message[0] === 'price must not be greater than 100000000':
          setOfferProposalErrorMessage('Ціна не повинна перевищувати 100 000 000 грн');
          break;
        default:
          setOfferProposalErrorMessage((error as ApiHttpException).message);
          break;
      }
    },
  });

  const deleteProposalMutation = useMutation({
    mutationFn: advertisementServiceDeleteAdvertisement<false>,
    onSuccess: ({ data }) => {
      if (data?.success) {
        router.replace(`/account/offers?update=${new Date().getTime()}`);
        setIsOpenDeleteModal(false);
      }
    },
  });

  const toggleFavorite = (itemId: string): void => {
    if (favorites.includes(itemId)) {
      setOpenFavoriteDeleteModal(true);
      setFavoriteRemovableId(itemId);
    } else {
      addFavorite(itemId);
    }
  };

  const confirmRemoveFavorite = () => {
    if (favoriteRemovableId) {
      removeFavorite(favoriteRemovableId);
      setFavoriteRemovableId(null);
      setOpenFavoriteDeleteModal(false);
    }
  };

  const handleClearPriceField = () => {
    setProposalPrice('');
  };

  const errorMessage = {
    message:
      'Для того, щоб надіслати пропозицію продавцю, необхідно запропонувати ціну на ділянку.',
  };

  const isMyAdverisment = user ? advertisement?.author.id === user.id : false;

  const isOnModeration = advertisement?.status === 'ON_MODERATION_REGISTRY_TIMEOUT';

  const sold = advertisement.status === 'DEAL_IS_DONE';

  const isAwaitingModeration = advertisement.status === 'ON_MODERATION_REGISTRY_CHECK_IN_PROGRESS';

  const notLoginnedFavoriteMessage =
    'Для того, щоб додавати в обрані зареєструйтесь або увійдіть в профіль, будь ласка.';

  const notLoginnedPriceProposalMessage =
    'Для того, щоб запропонувати ціну, зареєструйтесь або увійдіть в профіль, будь ласка.';

  const isNotActiveDelete = deals && !deals.find((deal) => deal?.status === 'ACCEPTED');

  const proposeModalContent = !offerProposalSuccessSended ? (
    <>
      <Close className={styles.closeBtn}>
        <CloseIcon />
      </Close>
      <Text type='h4'>Пропозиція ціни</Text>
      <Input
        error={checkError ? errorMessage : undefined}
        onInput={(e: React.ChangeEvent<HTMLInputElement>) => {
          const value = formatNumberValue(e.target.value, 7);

          setProposalPrice(value);
          if (e.target.value.length > 0) setCheckError(false);
        }}
        placeholder='Ціна'
        type='text'
        value={proposalPrice ? beautifyNumber(proposalPrice) : ''}
      />
      <div className={styles.btns}>
        <Close>
          <Button asDiv bordered>
            Скасувати
          </Button>
        </Close>
        <Button
          disabled={proposalMutation.isPending}
          onClick={() => {
            if (proposalPrice) {
              proposalMutation.mutate({
                body: {
                  advertisementId: advertisement.id,
                  price: Number(proposalPrice),
                },
                client: clientSdk,
              });
            } else {
              setCheckError(true);
            }
          }}
        >
          Далі
        </Button>
      </div>
      {offerProposalErrorMessage && (
        <Text color='red' style={{ marginTop: '-15px' }} type='p6'>
          {offerProposalErrorMessage}
        </Text>
      )}
      {proposalMutation.isPending && <Loader center />}
      <Text color='text-gray' type='p6'>
        Натискаючи кнопку{' '}
        <Text color='black' type='p6'>
          “Далі”
        </Text>
        , ви підтверджуєте, що ви громадянин України та володієте менше, ніж{' '}
        <Text color='black' type='p6'>
          100 га землі с/г призначення
        </Text>
      </Text>
    </>
  ) : (
    <>
      <Image alt='success' src={dobrozemOk} style={{ margin: 'auto' }} />
      <Text style={{ textAlign: 'center' }} type='p3'>
        Ваша пропозиція надіслана продавцю! Як тільки продавець розгляне пропозицію, ми сповістимо
        вас про його рішення
      </Text>
      <Close asChild onClick={() => setProposalPrice(undefined)}>
        <Button fullWIdth>Дякую</Button>
      </Close>
    </>
  );

  const actionBlock = (
    <div className={styles.actionBlock}>
      <button
        className={clsx(
          styles['btn-favorite'],
          clientUser && favorites.includes(advertisement?.id) && styles.isFavorite,
        )}
        onClick={() =>
          !user ? setOpenFavoriteModalNotLoginned(true) : toggleFavorite(advertisement?.id)
        }
      >
        <HeartIcon />
        <Text type='p3'>
          {clientUser && favorites.includes(advertisement?.id)
            ? 'Видалити зі списку обраних'
            : 'Додати до обраного'}
        </Text>
      </button>
      <div className={styles['btn-box']}>
        {advertisement.status !== 'DEAL_IN_PROGRESS' && (
          <>
            {user ? (
              <Modal
                contentClassName={styles.proposeModal}
                onOpenChange={handleClearPriceField}
                trigger={<Button small>Запропонувати ціну</Button>}
              >
                {proposeModalContent}
              </Modal>
            ) : (
              <Button onClick={() => setOpenPriceProposalModalNotLoginned(true)} small>
                Запропонувати ціну
              </Button>
            )}
          </>
        )}
        <OfferModalBtn className={styles['btn-ask']} isFull />
      </div>
    </div>
  );

  const myAdverismentActionBlock = (
    <>
      <div className={styles.myAdverismentActionBlock}>
        <div className={styles['btn-box']}>
          {!isOnModeration && !isAwaitingModeration && (
            <Link
              href={`/offer/edit?advertisementId=${advertisement.id}&edited=${new Date().getTime().toString()}`}
            >
              <Button asDiv className={styles.editBtn} small>
                <EditIcon /> Редагувати
              </Button>
            </Link>
          )}
          <OfferModalBtn className={styles['btn-ask']} isFull label='Допомога Доброзем' />
        </div>
        {isMyAdverisment && (
          <button
            className={styles.deleteBtn}
            onClick={() => {
              if (isNotActiveDelete) setIsOpenDeleteModal(true);
              else setIsOpenDeleteInDealModal(true);
            }}
          >
            <DeleteIcon />
            <Text type='button_small'>Видалити</Text>
          </button>
        )}
      </div>
      <StatusModal
        buttonsChildren={
          <>
            <div className={styles.deleteModalBtns}>
              <Button
                disabled={deleteProposalMutation.isPending}
                fullWIdth
                onClick={() =>
                  deleteProposalMutation.mutate({
                    client: clientSdk,
                    path: {
                      id: advertisement.id,
                    },
                  })
                }
              >
                Так, видалити
              </Button>
              <Button
                bordered
                fullWIdth
                onClick={() => {
                  setIsOpenDeleteModal(false);
                }}
              >
                Скасувати
              </Button>
            </div>
            {deleteProposalMutation.isPending && <Loader center />}
          </>
        }
        description='Ви дійсно бажаєте видалити оголошення? Всю інформацію про вашу ділянку буде видалено без можливості відновлення'
        logoStatus='sad'
        onOpenChange={setIsOpenDeleteModal}
        open={isOpenDeleteModal}
      />
      <StatusModal
        buttonsChildren={
          <>
            <div className={styles.deleteModalBtns}>
              <Button
                disabled={deleteProposalMutation.isPending}
                fullWIdth
                onClick={() => setIsOpenDeleteInDealModal(false)}
              >
                Добре, я почекаю
              </Button>
              <Button
                bordered
                fullWIdth
                onClick={() => {
                  setIsOpenDeleteInDealModal(false);
                  setIsOpenContactSupportModal(true);
                }}
              >
                Служба підтримки
              </Button>
            </div>
          </>
        }
        description='Ви не можете зараз видалити ваш профіль, адже у вас триває процес оформлення угоди'
        logoStatus='sad'
        onOpenChange={setIsOpenDeleteInDealModal}
        open={isOpenDeleteInDealModal}
      />
      <ContactSupportModal
        isOpen={isOpenContactSupportModal}
        setOpen={setIsOpenContactSupportModal}
      />
    </>
  );

  const isBuyersDealMode = currentDeal && getUserType(currentDeal);

  const buyersDealContent = currentDeal && currentDeal?.seller && (
    <BuyersDeal
      advertisementAuthor={currentDeal.seller}
      advertisementId={advertisement.id}
      dealData={
        getUserType(currentDeal) === 'seller' ? currentDeal?.sellerData : currentDeal?.buyerData
      }
      dealUserType={getUserType(currentDeal)}
    />
  );

  const mainPhotoStoragePath = (
    advertisement as AdvertisementResponse & {
      mainPhotoStoragePath?: null | string;
      systemData?: { mainPhotoStoragePath?: null | string };
    }
  ).mainPhotoStoragePath ??
    (
      advertisement as AdvertisementResponse & {
        systemData?: { mainPhotoStoragePath?: null | string };
      }
    ).systemData?.mainPhotoStoragePath ??
    null;
  const orderedGalleryPhotos = getOrderedAdvertisementLandPhotos(
    advertisement.landPhotos,
    mainPhotoStoragePath,
  );
  const hasGalleryPhotos = orderedGalleryPhotos.length > 0;
  const hasValidSatelliteImage = !isNoDataUpload(advertisement.land.satellite);
  const shouldShowSatelliteSlide = hasValidSatelliteImage || !hasGalleryPhotos;
  const satelliteSlideSrc = hasValidSatelliteImage
    ? uploadToSrc(advertisement.land.satellite)
    : '/no-data.svg';

  const geometry = advertisement.land.registry.geometry;

  const coordinates = (() => {
    if (Array.isArray(geometry)) {
      // Case 1: Array of coordinate objects
      if (
        typeof geometry[0] === 'object' &&
        'lat' in geometry[0] &&
        'lng' in geometry[0] &&
        typeof geometry[0].lat === 'number' &&
        typeof geometry[0].lng === 'number'
      ) {
        return { lat: geometry[0].lat, lng: geometry[0].lng };
      }

      // Case 2: Array of [lng, lat] pairs
      if (
        Array.isArray(geometry[0]) &&
        geometry[0].length === 2 &&
        typeof geometry[0][0] === 'number' &&
        typeof geometry[0][1] === 'number'
      ) {
        return { lat: geometry[0][1], lng: geometry[0][0] };
      }
    }

    return null;
  })();

  const notSpecified = 'Не вказано';

  // Priority: systemData.area > land.registry.area
  const displayArea = getAdvertisementArea(advertisement);

  useEffect(() => {
    setClientUser(user);
  }, [user]);

  return (
    <>
      <main className={styles.wrapper}>
        <Container>
          <div className={styles['top-content']}>
            <div className={styles['top-left']}>
              <Swiper
                autoplay={{
                  delay: 4000,
                  disableOnInteraction: false,
                }}
                className='mySwiper'
                modules={[Pagination, Autoplay]}
                pagination={pagination}
                rewind={true}
                spaceBetween={20}
              >
                {shouldShowSatelliteSlide && (
                  <SwiperSlide>
                    <div
                      className={clsx(
                        styles['slider-item'],
                        !hasValidSatelliteImage && styles.noImage,
                      )}
                      onClick={() => {
                        if (hasGalleryPhotos || hasValidSatelliteImage)
                          setIsOpenModal(true);
                      }}
                    >
                      <img alt='' src={satelliteSlideSrc} />
                    </div>
                  </SwiperSlide>
                )}
                {orderedGalleryPhotos.map((photo, index) => {
                  return (
                    <SwiperSlide key={index}>
                      <div
                        className={styles['slider-item']}
                        onClick={() => {
                          if (orderedGalleryPhotos.length) setIsOpenModal(true);
                          if (shouldShowSatelliteSlide) {
                            setSelectImageIndex(index + 1);
                          } else {
                            setSelectImageIndex(index);
                          }
                        }}
                      >
                        <Image
                          alt='plot of land'
                          height={500}
                          src={uploadToSrc(photo)}
                          width={500}
                        />
                      </div>
                    </SwiperSlide>
                  );
                })}
              </Swiper>
            </div>
            <div className={styles['top-right']}>
              <div className={styles['status-wrapper']}>
                {advertisement && (
                  <OfferTag advertisement={advertisement} user={user ?? undefined} />
                )}
                <div className={styles['top-info']}>
                  <Text color='text-gray' type='p4'>
                    Створено: {dateFormatterShort.format(advertisement.createdAt)}
                  </Text>
                  <div className={styles['viewedLike-wrapper']}>
                    <Image alt='' src={eyeIcon} />
                    <Text color='text-gray' type='p4'>
                      {advertisement.analytics.viewsCounts}
                    </Text>
                  </div>
                  <div className={styles['viewedLike-wrapper']}>
                    <Image alt='' src={heartIcon} />
                    <Text color='text-gray' type='p4'>
                      {advertisement.analytics.favoritesCount}
                    </Text>
                  </div>
                </div>
              </div>
              <div className={styles['middle-info']}>
                <Text type='h4'>
                  {displayArea?.toFixed?.(4) ?? 'Не вказано'} га /{' '}
                  {advertisement.land.registry.location ||
                    `${advertisement.land.district?.name} район ; ${advertisement.land.region?.name} обл.`}
                </Text>
                <Text color='text-gray' type='p3'>
                  <IdField id={advertisement?.id} />
                </Text>
                {coordinates && (
                  <a
                    className={styles['map-link']}
                    href={`https://www.google.com/maps?q=${coordinates.lat},${coordinates.lng}`}
                    rel='noopener noreferrer'
                    target='_blank'
                  >
                    <MapPinIcon />
                    <Text type='p3'>Переглянути на мапі</Text>
                  </a>
                )}
              </div>
              <div className={styles['bottom-right']}>
                {advertisement?.profitability && (
                  <Text color='blue' type='p3'>
                    Дохідність: {advertisement?.profitability}%
                  </Text>
                )}
                <div className={styles['price-wrapper']}>
                  <Text type='p2'>
                    {advertisement.price
                      ? `${beautifyNumber(advertisement.price)} ₴`
                      : 'Ціна не вказана'}{' '}
                  </Text>
                  {(advertisement.price as number) > 0 && (
                    <TooltipComponent className={styles['tooltip-price']} side='top'>
                      <Text className={styles['first-text']} type='p4'>
                        Це ціна земельної ділянки, яка не включає витрати, пов&apos;язані з
                        проведенням угоди купівлі-продажу.
                      </Text>
                      <div>
                        <Text type='p4'>Покупцеві потрібно буде додатково оплатити:</Text>
                      </div>
                      <div>
                        <Text color='blue' tag='span' type='p4'>
                          Нотаріальні послуги
                        </Text>
                        <Text type='p4'> - вартість залежить від нотаріуса</Text>
                      </div>
                      <div>
                        <Text color='blue' tag='span' type='p4'>
                          Комісію Доброзему
                        </Text>
                        <Text type='p4'> - 7% від суми договору, але не менше 5000 грн</Text>
                      </div>
                    </TooltipComponent>
                  )}
                  {(advertisement.price as number) > 0 && (
                    <Text color='text-gray' type='p4'>
                      {advertisement.price &&
                        displayArea &&
                        `(${beautifyNumber((advertisement.price / displayArea).toFixed(1))} ₴ / га)`}
                    </Text>
                  )}
                </div>
              </div>
              <Responsive className={styles['btn-wrapper']} showFrom='laptop'>
                {isMyAdverisment ? myAdverismentActionBlock : actionBlock}
              </Responsive>
            </div>
          </div>
          <Responsive className={styles['btn-wrapper']} hideFrom='laptop'>
            {isMyAdverisment ? myAdverismentActionBlock : actionBlock}
          </Responsive>
          {isBuyersDealMode && buyersDealContent}
          {deals && deals.length > 0 && <DealHistory deals={deals as Offer[]} sold={sold} />}
          <div className={styles['bottom-content']}>
            <div className={styles['bottom-info-wrapper']}>
              <div className={styles['bottom-info-item']}>
                <div className={styles['info-title-wrapper']}>
                  <Text color='text-gray' type='p4'>
                    Статус оренди
                  </Text>
                  <TooltipComponent className={styles['tooltip-status']} side='bottom'>
                    <Text type='p4'>
                      Дані про оренду цієї ділянки отримані від продавця, але не підтверджені
                      записами в офіційних реєстрах
                    </Text>
                  </TooltipComponent>
                </div>
                {(() => {
                  if (advertisement?.rent.status === 'RENTED') {
                    return (
                      <Text type='p3'>
                        {advertisement.rent.endYear
                          ? `В оренді до ${advertisement.rent.endYear} року`
                          : 'В оренді'}
                      </Text>
                    );
                  } else if (advertisement?.rent.status === 'NOT_FOR_RENT') {
                    return <Text type='p3'>Не здається в оренду</Text>;
                  } else {
                    return <Text type='p3'>Не вказано</Text>;
                  }
                })()}
              </div>
              <div className={styles['bottom-info-item']}>
                <div className={styles['info-title-wrapper']}>
                  <Text color='text-gray' type='p4'>
                    Загальна орендна плата за рік
                  </Text>
                  <TooltipComponent className={styles['tooltip-status']} side='bottom'>
                    <Text type='p4'>
                      Дані про оренду цієї ділянки отримані від продавця, але не підтверджені
                      записами в офіційних реєстрах
                    </Text>
                  </TooltipComponent>
                </div>
                <AuthorizedInfo>
                  <Text type='p3'>
                    {advertisement.rent.pricePerYear && advertisement.rent.pricePerYear > 0
                      ? `${beautifyNumber(Number(advertisement.rent.pricePerYear))} грн`
                      : notSpecified}
                  </Text>
                </AuthorizedInfo>
              </div>
              <div className={styles['bottom-info-item']}>
                <div className={styles['info-title-wrapper']}>
                  <Text color='text-gray' type='p4'>
                    Орендар ділянки
                  </Text>
                </div>
                <AuthorizedInfo>
                  <Text type='p3'>
                    {advertisement.land.registry.tenant.name
                      ? advertisement.land.registry.tenant.name
                      : notSpecified}
                  </Text>
                </AuthorizedInfo>
              </div>
              <div className={styles['bottom-info-item']}>
                <div className={styles['info-title-wrapper']}>
                  <Text color='text-gray' type='p4'>
                    Вид угідь
                  </Text>
                </div>
                <Text type='p3'>
                  {advertisement.land.registry.landType
                    ? advertisement.land.registry.landType
                    : notSpecified}
                </Text>
              </div>
              <div className={styles['bottom-info-item']}>
                <div className={styles['info-title-wrapper']}>
                  <Text color='text-gray' type='p4'>
                    Цільове призначення ділянки
                  </Text>
                </div>
                <Text type='p3'>
                  {advertisement.land.registry.purpose
                    ? decodeHtmlEntities(advertisement.land.registry.purpose)
                    : notSpecified}
                </Text>
              </div>
              <div className={styles['bottom-info-item']}>
                <div className={styles['info-title-wrapper']}>
                  <Text color='text-gray' type='p4'>
                    Нормативна грошова оцінка
                  </Text>
                </div>
                <AuthorizedInfo>
                  <Text type='p3'>
                    {advertisement.land.registry.price
                      ? `${beautifyNumber(advertisement.land.registry.price)} грн`
                      : notSpecified}
                  </Text>
                </AuthorizedInfo>
              </div>
              <div className={styles['bottom-info-item']}>
                <div className={styles['info-title-wrapper']}>
                  <Text color='text-gray' type='p4'>
                    Технічні особливості
                  </Text>
                </div>
                <AuthorizedInfo>
                  <div className={styles['tech-wrapper']}>
                    {advertisement.landTechnicalFeatures.length > 0 ? (
                      advertisement.landTechnicalFeatures.map((item, index) => (
                        <Text key={index} type='p3'>
                          {`${technicalFeaturesDictionary[item]}${advertisement.landTechnicalFeatures.length > 1 && index < advertisement.landTechnicalFeatures.length - 1 ? ',' : ''}`}
                        </Text>
                      ))
                    ) : (
                      <Text type='p3'>{notSpecified}</Text>
                    )}
                  </div>
                </AuthorizedInfo>
              </div>
              <div className={styles['bottom-info-item']}>
                <div className={styles['info-title-wrapper']}>
                  <Text color='text-gray' type='p4'>
                    Продавець
                  </Text>
                </div>
                <Text type='p3'>
                  {advertisement.owner.firstName} {advertisement.owner.lastName[0]}.
                </Text>
              </div>
              <div className={styles['bottom-info-item']}>
                <div className={styles['info-title-wrapper']}>
                  <Text color='text-gray' type='p4'>
                    Опис ділянки
                  </Text>
                </div>
                <Text type='p3'>{advertisement?.description || 'Не вказано'}</Text>
              </div>
            </div>

            {advertisement.land.statistics?.crop?.length ? (
              <div className={styles['table']}>
                <Text type='p3'>Сівозміна за останні роки</Text>
                <div className={styles['table-wrapper']}>
                  <div
                    className={styles['table-left']}
                    style={{
                      gridTemplateColumns: `repeat(${(advertisement.land.statistics?.crop?.length ?? 0) + 1}, 1fr)`,
                    }}
                  >
                    <div className={styles['table-item']}>
                      <Text type='p4'>Рік</Text>
                    </div>
                    {advertisement.land.statistics?.crop
                      ?.sort((a, b) => a.year - b.year) // сортировка по годам
                      .map(({ year }, index) => (
                        <div className={styles['table-item']} key={index}>
                          <Text color='text-gray' type='p4'>
                            {year}
                          </Text>
                        </div>
                      ))}
                  </div>
                  <div
                    className={styles['table-right']}
                    style={{
                      gridTemplateColumns: `repeat(${(advertisement.land.statistics?.crop?.length ?? 0) + 1}, 1fr)`,
                    }}
                  >
                    <div className={styles['table-item']}>
                      <Text type='p4'>Культура</Text>
                    </div>
                    {advertisement.land.statistics?.crop
                      ?.sort((a, b) => a.year - b.year) // сортировка по годам
                      .map(({ crop }, index) => (
                        <div className={styles['table-item']} key={index}>
                          <Text color='text-gray' type='p4'>
                            {crop}
                          </Text>
                        </div>
                      ))}
                  </div>
                </div>
              </div>
            ) : null}
          </div>
        </Container>
      </main>
      <StatusModal
        buttonsChildren={
          <>
            <Link href={'/auth/sign-in'}>
              <Button
                asDiv
                fullWIdth
                onClick={() => {
                  setOpenFavoriteModalNotLoginned(false);
                  setOpenPriceProposalModalNotLoginned(false);
                }}
              >
                Увійти
              </Button>
            </Link>
            <Link href={'/auth/registration'}>
              <Button
                asDiv
                bordered
                fullWIdth
                onClick={() => {
                  setOpenFavoriteModalNotLoginned(false);
                  setOpenPriceProposalModalNotLoginned(false);
                }}
              >
                Зареєструватись
              </Button>
            </Link>
          </>
        }
        description={
          isOpenFavoriteModalNotLoginned
            ? notLoginnedFavoriteMessage
            : notLoginnedPriceProposalMessage
        }
        logoStatus='smile'
        onOpenChange={
          isOpenFavoriteModalNotLoginned
            ? setOpenFavoriteModalNotLoginned
            : setOpenPriceProposalModalNotLoginned
        }
        open={isOpenFavoriteModalNotLoginned || isOpenPriceProposalModalNotLoginned}
      />
      <Modal
        className={styles['modal']}
        contentClassName={styles['mainContent-modal']}
        onOpenChange={setIsOpenModal}
        open={isOpenModal}
      >
        <div className={styles['modal-content']}>
          <div className={styles['top-modal']}>
            <div className={styles['btn-close']} onClick={() => setIsOpenModal(false)}>
              <svg
                fill='none'
                height='45'
                viewBox='0 0 44 45'
                width='44'
                xmlns='http://www.w3.org/2000/svg'
              >
                <path
                  clipRule='evenodd'
                  d='M25.9258 35.3333L29.333 31.926L18.9902 21.5833L29.333 11.2405L25.9258 7.83325L12.1758 21.5833L25.9258 35.3333Z'
                  fill='black'
                  fillRule='evenodd'
                />
              </svg>
            </div>
            <div className={styles['modal-title-wrapper']}>
              <Text className={styles['modal-title']} tag='p' type='p1'>
                {displayArea?.toFixed?.(4) ?? 'Не вказано'} га /{' '}
                {advertisement.land.registry.location ||
                  `${advertisement.land.district?.name} район ; ${advertisement.land.region?.name} обл.`}
              </Text>
              <Text className={styles['modal-id']} color='text-gray' tag='p' type='p3'>
                <IdField id={advertisement.id} />
              </Text>
            </div>
          </div>
          <div className={styles['slider-modal']}>
            <Swiper
              className={styles['modal-swiper']}
              initialSlide={selectedImageIndex}
              modules={[Pagination, Autoplay]}
              onSwiper={setSliderControls}
              pagination={pagination}
              rewind={true}
              spaceBetween={20}
            >
              {shouldShowSatelliteSlide && (
                <SwiperSlide>
                  <div className={styles['slider-item-modal']}>
                    <img alt='' src={satelliteSlideSrc} />
                  </div>
                </SwiperSlide>
              )}
              {orderedGalleryPhotos.map((photo, index) => {
                return (
                  <SwiperSlide key={index}>
                    <div className={styles['slider-item-modal']}>
                      <Image
                        alt=''
                        fill
                        sizes='(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw'
                        src={uploadToSrc(photo)}
                      />
                    </div>
                  </SwiperSlide>
                );
              })}
            </Swiper>
            {orderedGalleryPhotos.length > 0 && (
              <Responsive showFrom='laptop'>
                <div className={styles['btn-left']} onClick={() => sliderControls?.slidePrev()}>
                  <SliderControlLeft />
                </div>
                <div className={styles['btn-right']} onClick={() => sliderControls?.slideNext()}>
                  <SliderControlRight />
                </div>
              </Responsive>
            )}
          </div>
        </div>
      </Modal>
      <StatusModal
        buttonsChildren={
          <>
            <Button fullWIdth onClick={confirmRemoveFavorite}>
              Так, я хочу видалити
            </Button>
            <Button
              bordered
              fullWIdth
              onClick={() => {
                setFavoriteRemovableId(null);
                setOpenFavoriteDeleteModal(false);
              }}
            >
              Ні, не видаляти
            </Button>
          </>
        }
        description='Ви впевнені що бажаєте видалити це оголошення зі списку обраних?'
        logoStatus='sad'
        onOpenChange={setOpenFavoriteDeleteModal}
        open={isOpenFavoriteDeleteModal}
      />
    </>
  );
};
